import React, { useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { AnimatePresence, motion } from 'framer-motion';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import { Markdown } from '+/components/Markdown';
import { passChildren } from '+/components/Markdown/Markdown.component';
import { getLocalDateString } from '+/utils/timeZonesHelper';

import PropTypes from '../instances/propTypesInstance';

import { StyledDiv, StyledSpan } from './elements/containers';
import {
  StyledCheckbox,
  StyledClearX,
  StyledErrorMessage,
  StyledInput,
  StyledLabel,
  StyledOption,
  StyledRadioButton,
  StyledSelect,
  StyledTextArea,
  StyledPasswordToggle,
  StyledNote,
  StyledCheckboxLabel,
} from './elements/forms';
import StyledTooltip, { StyledQuestionIcon } from './StyledTooltip';

const errorVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const propsToSelect = ({ placeholder = null, ...props }) => (
  <StyledSelect {...props} data-idf={`select-${props.name}`}>
    <StyledOption aria-label={props.name} value="">
      {placeholder}
    </StyledOption>
    {props.options.map(option => (
      <StyledOption key={option.value} aria-label={option.value} value={option.value}>
        {option.label}
      </StyledOption>
    ))}
  </StyledSelect>
);

propsToSelect.propTypes = {
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
};

// Return datetime-local, should fallback to input with input if unavailable
const propsToDatetime = ({ value = null, ...props }) => (
  <StyledInput placeholder="yyyy-mm-ddThh:mm" value={getLocalDateString(value)?.slice(0, 16)} {...props} type="datetime-local" />
);

propsToDatetime.propTypes = {
  value: PropTypes.oneOf([PropTypes.string, PropTypes.instanceOf(Date)]),
};

const typesToComponents = {
  textArea: props => {
    const { checkToggle, error, value, innerRef, ...otherProps } = props;

    return <StyledTextArea ref={innerRef} error={error ? 'true' : ''} value={value || ''} {...otherProps} />;
  },
  checkbox: StyledCheckbox,
  radio: StyledRadioButton,
  select: propsToSelect,
  datetime: propsToDatetime,
  phone: props => <PatternFormat {...props} customInput={StyledInput} format="(###) ###-####" mask="_" />,
};

const withMarkdown = text => (
  <Markdown
    source={text}
    renderers={{
      paragraph: passChildren(React.Fragment),
      root: passChildren(React.Fragment),
    }}
  />
);

// TODO cleanup check boxes.
const StyledInputBlock = ({
  touched,
  label = '',
  submitCount = 0,
  fieldType = 'text',
  name,
  handleChange,
  handleBlur = null,
  handleClear = null,
  values,
  errors = undefined,
  options = [],
  disabled = false,
  tooltip = null,
  danger = null,
  placeholder = null,
  checkToggle = false,
  smallFont = false,
  formal = false,
  forwardedRef = null,
  validators = null,
  customError = null,
  decorative = false,
  note = null,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const altered = name.split('.').reduce((accum, part) => accum && accum[part], touched) || submitCount > 0;
  const StyledInputType = typesToComponents[fieldType] || StyledInput;
  const value = name
    .replace(/\[(.*)\]/g, (_m, p1) => `.${p1}`)
    .split('.')
    .reduce((accum, part) => accum[part], values);
  let error = name.split('.').reduce((accum, part) => accum && accum[part], errors);
  const InputWrapper = handleClear ? StyledDiv : React.Fragment;
  const isRequired = validators && validators.fields[name].tests.map(t => t.OPTIONS.name).includes('required');
  const labelWithRequiredMarker = isRequired ? `${label} *` : label;

  if (error) {
    error = customError || error;
  }

  return (
    <StyledLabel
      smallFont={smallFont}
      formal={formal}
      error={!!error && ['checkbox', 'radio'].includes(fieldType)}
      inline={['checkbox', 'radio'].includes(fieldType)}
    >
      {(['checkbox', 'radio'].includes(fieldType) && !checkToggle) || <span aria-hidden="true">{withMarkdown(labelWithRequiredMarker)}</span>}
      {tooltip && (
        <StyledSpan>
          <StyledQuestionIcon />
          <StyledTooltip hoverable={StyledSpan} text={tooltip} />
        </StyledSpan>
      )}
      <InputWrapper>
        <StyledInputType
          value={value}
          name={name}
          options={options}
          type={fieldType === 'password' && showPassword ? 'text' : fieldType}
          onChange={handleChange}
          onClick={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          danger={danger}
          error={!!error}
          placeholder={placeholder}
          checkToggle={checkToggle}
          innerRef={forwardedRef}
          aria-label={label}
          decorative={decorative}
        />
        {handleClear && <StyledClearX onClick={handleClear} />}
        {fieldType === 'password' && (
          <StyledPasswordToggle
            onClick={() => setShowPassword(!showPassword)}
            title={`${showPassword ? 'Hide' : 'Show'} password`}
            aria-label={`${showPassword ? 'Hide' : 'Show'} password`}
            error={altered && error}
            type="button"
            tabIndex="-1"
          >
            {!showPassword ? <FaEye style={{ width: '2rem', height: '2rem' }} /> : <FaEyeSlash style={{ width: '3rem', height: '2rem' }} />}
          </StyledPasswordToggle>
        )}
      </InputWrapper>
      {['checkbox', 'radio'].includes(fieldType) && !checkToggle && (
        <StyledCheckboxLabel smallFont={smallFont} aria-hidden="true">
          {withMarkdown(label)}
        </StyledCheckboxLabel>
      )}
      <AnimatePresence>
        {altered && error && (
          <StyledErrorMessage variants={errorVariants} as={motion.div} initial="hidden" animate="visible" exit="hidden" transform={{ duration: 0.2 }}>
            {' '}
            {error}{' '}
          </StyledErrorMessage>
        )}
      </AnimatePresence>
      {!(altered && error) && note && <StyledNote>{note}</StyledNote>}
    </StyledLabel>
  );
};

StyledInputBlock.propTypes = {
  label: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  touched: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  values: PropTypes.object.isRequired,
  note: PropTypes.string,
  name: PropTypes.string.isRequired,
  fieldType: PropTypes.string,
  formal: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  handleClear: PropTypes.func,
  submitCount: PropTypes.number,
  tooltip: PropTypes.node,
  danger: PropTypes.bool,
  placeholder: PropTypes.string,
  checkToggle: PropTypes.bool,
  forwardedRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(HTMLInputElement), PropTypes.instanceOf(HTMLTextAreaElement)]),
  }),
  smallFont: PropTypes.bool,
  validators: PropTypes.yupValidators,
  customError: PropTypes.string,
  decorative: PropTypes.bool,
};

export default StyledInputBlock;
